import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageLayout from 'layouts/PageLayout';
import { GetAdvertById } from 'services/AdvertsService';
import Snackbar from '@mui/material/Snackbar';
import Alert from 'components/Alert';
import AdvertEditPageContent from 'components/Content/AdvertEditPageContent';
import PageContentLayout from 'layouts/PageContentLayout';

export default function AdvertEdit() {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
  const [warningMessage, setWarningMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [summaryBar, setSummaryBar] = useState(<></>);
  const navigate = useNavigate();

  const advertId = useMemo(() => {
    const parsedId = +(params.id ?? '0');
    if (isNaN(parsedId)) return 0;
    return parsedId;
  }, [params.id]);

  useEffect(() => {
    const getData = async () => {
      const res = await GetAdvertById(advertId);
      if (res === null) navigate('/not-found');
    };
    getData();
  }, [advertId, navigate]);

  const handleMessage = useCallback(
    (e: MessageEvent) => {
      if (e.data === 'OK') navigate(`/adverts/${advertId}`);
    },
    [advertId, navigate]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return (
    <>
      <Snackbar
        open={successMessage !== ''}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={errorMessage !== ''} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ whiteSpace: 'pre-line' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={warningMessage !== ''} onClose={() => setWarningMessage('')}>
        <Alert severity="warning" onClose={() => setWarningMessage('')}>
          {warningMessage}
        </Alert>
      </Snackbar>
      <PageLayout SummaryBar={summaryBar}>
        <PageContentLayout showLoading={isLoading}>
          <AdvertEditPageContent
            advertId={advertId}
            setSummaryBar={setSummaryBar}
            errorHandler={setErrorMessage}
            loadingHandler={setIsLoading}
            successHandler={setSuccessMessage}
            warningHandler={setWarningMessage}
          />
        </PageContentLayout>
      </PageLayout>
    </>
  );
}
